/**
 * 通用计费套餐工具
 * 不依赖具体支付供应商，Creem / Lemon Squeezy / Stripe / Paddle 均可复用
 */

/** 套餐等级（数字越大越高）：用于判断升级 vs 降级 */
export const PLAN_RANK: Record<string, number> = {
  PLAN_BASIC_MONTHLY: 1,
  PLAN_BASIC_YEARLY: 2,
  PLAN_PRO_MONTHLY: 3,
  PLAN_PRO_YEARLY: 4,
  // Tenant plans
  PLAN_TENANT_TEAM_MONTHLY: 10,
  PLAN_TENANT_TEAM_YEARLY: 11,
  PLAN_TENANT_BUSINESS_MONTHLY: 12,
  PLAN_TENANT_BUSINESS_YEARLY: 13,
  PLAN_TENANT_STARTUP_MONTHLY: 14,
  PLAN_TENANT_STARTUP_YEARLY: 15,
  PLAN_TENANT_EDUCATION_MONTHLY: 16,
  PLAN_TENANT_EDUCATION_YEARLY: 17,
  PLAN_TENANT_ENTERPRISE_MONTHLY: 18,
  PLAN_TENANT_ENTERPRISE_YEARLY: 19,
};

/** 判断是否为租户套餐 */
export function isTenantPlanCode(planCode: string): boolean {
  return planCode.toUpperCase().startsWith("PLAN_TENANT_");
}

/** 一次性购买套餐前缀（非订阅，不参与续费与升降级） */
export const ONE_TIME_PLAN_PREFIXES = [
  "PLAN_TOPUP_",
  "PLAN_TUTOR_",
  "PLAN_COURSE_",
] as const;

/** 判断是否为一次性购买套餐 */
export function isOneTimePlanCode(
  planCode: string | null | undefined,
): boolean {
  if (!planCode) return false;
  const pc = planCode.toUpperCase();
  return ONE_TIME_PLAN_PREFIXES.some((prefix) => pc.startsWith(prefix));
}

/** 将 planCode 转换为友好的显示名称 */
export function getPlanDisplayName(planCode: string): string {
  const pc = planCode.toUpperCase();

  // 个人套餐
  if (pc === "PLAN_FREE_MONTHLY") return "Free";
  if (pc === "PLAN_BASIC_MONTHLY") return "Basic Monthly";
  if (pc === "PLAN_BASIC_YEARLY") return "Basic Yearly";
  if (pc === "PLAN_PRO_MONTHLY") return "Premium Monthly";
  if (pc === "PLAN_PRO_YEARLY") return "Premium Yearly";

  // 租户套餐
  if (pc === "PLAN_TENANT_TEAM_MONTHLY") return "Team Monthly";
  if (pc === "PLAN_TENANT_TEAM_YEARLY") return "Team Yearly";
  if (pc === "PLAN_TENANT_BUSINESS_MONTHLY") return "Business Monthly";
  if (pc === "PLAN_TENANT_BUSINESS_YEARLY") return "Business Yearly";
  if (pc === "PLAN_TENANT_EDUCATION_MONTHLY") return "Education Monthly";
  if (pc === "PLAN_TENANT_EDUCATION_YEARLY") return "Education Yearly";
  if (pc === "PLAN_TENANT_STARTUP_MONTHLY") return "Startup Monthly";
  if (pc === "PLAN_TENANT_STARTUP_YEARLY") return "Startup Yearly";
  if (pc === "PLAN_TENANT_ENTERPRISE_MONTHLY") return "Enterprise Monthly";
  if (pc === "PLAN_TENANT_ENTERPRISE_YEARLY") return "Enterprise Yearly";

  // Enterprise 销售合同
  if (pc === "PLAN_ENTERPRISE_CONTRACT") return "Enterprise Contract";

  // 一次性购买
  if (pc === "PLAN_TOPUP_1K") return "AI Quota Top-up (1,000)";
  if (pc === "PLAN_TOPUP_3K") return "AI Quota Top-up (3,000)";
  if (pc === "PLAN_TOPUP_8K") return "AI Quota Top-up (8,000)";
  if (pc === "PLAN_TUTOR_30MIN") return "AI Tutor 30 Minutes";
  if (pc === "PLAN_TUTOR_60MIN") return "AI Tutor 60 Minutes";
  if (pc === "PLAN_COURSE_SINGLE") return "Single Course";
  if (pc === "PLAN_COURSE_BUNDLE_5") return "Course Bundle (5 Courses)";
  if (pc === "PLAN_COURSE_YEARLY_PASS") return "Course Yearly Pass";

  // Add-on 增值包
  if (pc === "ADDON_VOCAB_PRO_MONTHLY") return "Pro Vocabulary Pack";
  if (pc === "ADDON_AI_ROLE_BUSINESS_MONTHLY") return "Business AI Pack";
  if (pc === "ADDON_AI_ROLE_TRAVEL_MONTHLY") return "Travel AI Pack";
  if (pc === "ADDON_AI_ROLE_ACADEMIC_MONTHLY") return "Academic AI Pack";

  // 租户额外席位 add-on
  const seatMatch = pc.match(
    /^PLAN_TENANT_(TEAM|BUSINESS|EDUCATION|STARTUP|ENTERPRISE)_SEAT_(MONTHLY|YEARLY)$/,
  );
  if (seatMatch) {
    const tierNames: Record<string, string> = {
      TEAM: "Team",
      BUSINESS: "Business",
      EDUCATION: "Education",
      STARTUP: "Startup",
      ENTERPRISE: "Enterprise",
    };
    const cycle = seatMatch[2] === "MONTHLY" ? "Monthly" : "Yearly";
    return `${tierNames[seatMatch[1]]} Extra Seat (${cycle})`;
  }

  // 默认返回 planCode
  return planCode;
}
